import { tr } from '@/lib/i18n-core';
import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Banknote } from 'lucide-react';
import { post } from '@/lib/api';
import { describeError } from '@/lib/auth';
import { todayIso, toIsoDate } from '@/lib/utils';
import { minorFromInput } from '@/components/MoneyInput';
import { FormActions, FormScreen, FormSection } from '@/components/ui/form-screen';
import { Alert, DatePicker, Field, Input } from '@/components/ui/primitives';
import { useToast } from '@/components/ui/toast';
import { safeReturnTo } from '../opd/BookAppointmentPage';
import { HR_TAB_PATH } from './employee-options';

const monthStart = () => {
  const now = new Date();
  return toIsoDate(new Date(now.getFullYear(), now.getMonth(), 1));
};

export function RunPayrollPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const returnTo = safeReturnTo(params.get('returnTo'), HR_TAB_PATH.payroll);
  const [initial] = useState(() => ({ periodStart: monthStart(), periodEnd: todayIso(), overtimeRate: '0', taxPct: '0' }));
  const [form, setForm] = useState(initial);
  const [error, setError] = useState<string | null>(null);

  const run = useMutation({
    mutationFn: () =>
      post('/hr/payroll', {
        periodStart: form.periodStart,
        periodEnd: form.periodEnd,
        overtimeRatePerHourMinor: minorFromInput(form.overtimeRate),
        taxPct: Number(form.taxPct || 0),
      }),
    onSuccess: () => {
      toast.success('Draft payslips built', 'Re-running the same period replaces them');
      void queryClient.invalidateQueries({ queryKey: ['payroll'] });
      navigate(returnTo, { replace: true });
    },
    onError: (caught) => setError(describeError(caught)),
  });

  const dirty = !run.isSuccess && JSON.stringify(form) !== JSON.stringify(initial);

  return (
    <FormScreen
      title="Run payroll"
      description="Absent days are deducted pro-rata; approved leave is paid"
      icon={Banknote}
      backTo={returnTo}
      dirty={dirty}
      onSubmit={(event) => {
        event.preventDefault();
        setError(null);
        run.mutate();
      }}
    >
      <FormSection title={tr('admin', 'payroll')} icon={Banknote}>
        <Field label="From" required>
          <DatePicker value={form.periodStart} onChange={(value) => setForm({ ...form, periodStart: value })} />
        </Field>
        <Field label="To" required>
          <DatePicker value={form.periodEnd} onChange={(value) => setForm({ ...form, periodEnd: value })} />
        </Field>
        <Field label="Overtime rate per hour">
          <Input type="text" dir="ltr" inputMode="decimal" value={form.overtimeRate} onChange={(e) => setForm({ ...form, overtimeRate: e.target.value })} />
        </Field>
        <Field label="Tax %">
          <Input type="number" step="0.1" min={0} max={100} value={form.taxPct} onChange={(e) => setForm({ ...form, taxPct: e.target.value })} />
        </Field>
      </FormSection>

      {error && <Alert variant="destructive">{error}</Alert>}

      <FormActions submitLabel="Build payslips" loading={run.isPending} cancelTo={returnTo} />
    </FormScreen>
  );
}
